/**
 * Public endpoints (docs/53 §6.2): the site form and events beacon on `sites`, and the prospect page,
 * shared report and checkout start on `app`. Tenant always comes from the host or a signed slug, never a body.
 */
import { z } from 'zod';
import { Day, Email, HttpsUrl, IsoDateTime, Month, PhoneInput, text } from '../common.ts';
import { EventMetric, Plan } from '../enums.ts';
import { Report } from './reports.ts';

/** `POST /_f/lead` form fields, `application/x-www-form-urlencoded`. Field names are short and stable. */
export const SiteLeadForm = z
  .object({
    name: text(120),
    phone: PhoneInput.optional(),
    email: Email.optional(),
    message: text(5000).optional(),
    service: z.string().max(64).optional(),
    town: z.string().max(80).optional(),
    /** Page the form sat on (a path on the same host). */
    page: z.string().regex(/^\/[^\s]{0,511}$/),
    /** Checkbox value when the form carries the text consent box (docs/50 §4.3 auto-reply, v1.1). */
    sms_consent: z.literal('1').optional(),
    consent_v: z.string().max(40).optional(),
    /** Honeypot: must be empty. */
    website: z.string().max(0).optional(),
    /** Render timestamp in ms for the minimum fill time check. */
    ts: z.string().regex(/^\d{10,14}$/),
  })
  .strict()
  .refine((f) => f.phone !== undefined || f.email !== undefined, { message: 'a phone or email is needed', path: ['phone'] });

export const SITE_FORM_LIMITS = { minFillMs: 2500, maxBodyBytes: 16_384 } as const;

/** `POST /_e` beacon: a small batch of events from one page view. No cookies, fixed vocabulary. */
export const SiteEventBeacon = z
  .object({
    p: z.string().regex(/^\/[^\s]{0,511}$/),
    e: z.array(z.object({ m: EventMetric, t: z.number().int().min(0).max(3_600_000) }).strict()).min(1).max(20),
  })
  .strict();

/** `POST /checkout/start` from a prospect page. */
export const CheckoutStartBody = z
  .object({
    slug: z.string().regex(/^[a-z0-9]{16,40}$/),
    plan: Plan,
    email: Email,
    consents: z
      .object({ terms: z.literal(true), autoRenewal: z.literal(true), aiDrafting: z.literal(true), ownerAuthority: z.literal(true) })
      .strict(),
    turnstileToken: z.string().max(4096).optional(),
  })
  .strict();
export const CheckoutStarted = z.object({ url: HttpsUrl });

/** Data behind `/p/:slug` (noindex, expires at 30 days). */
export const ProspectPage = z.object({
  slug: z.string().max(40),
  businessName: z.string().max(60),
  /** Null on a reply recon page, which frames no built site (R6-C6, c051 open). */
  previewUrl: z.string().max(2048).nullable(),
  expiresOn: Day,
  map: z.object({ keyword: z.string().max(120), top3Points: z.number().int().min(0).max(81), pointsPath: z.string().max(512) }).nullable(),
  eligible: z.boolean(),
  plans: z.array(z.object({ plan: Plan, label: z.string().max(80), setupCents: z.number().int().min(0), recurringCents: z.number().int().min(0) })),
});

/** Data behind `/r/:token`: a read-only report, noindex, 14-day expiry. */
export const SharedReport = z.object({
  businessName: z.string().max(60),
  month: Month,
  report: Report.omit({ pdfUrl: true }),
  expiresAt: IsoDateTime,
});
